'use client';

/**
 * Auth security gate — client state for the full-screen captcha flow.
 *
 * The gate replaces the inline captcha on the auth pages: instead of an
 * extra field inside the login/reset forms, a flagged browser gets a
 * full-screen multi-step verification before it can use the page at all.
 *
 * The gate is required when:
 *  - the server answered an auth request with `requires_captcha`, or
 *  - a remembered identifier has hit the local failure threshold.
 *
 * Passing the gate yields a single-use pass token (sessionStorage) that is
 * sent as `captcha_token` on the next auth call; the server redeems it and
 * clears its own failure counters, so a verified user starts fresh.
 */

import { buildApiUrl } from '@/lib/platformUtils';
import { getPortalAppHeaders } from '@/lib/pwaAppIdentity';

// Reuses the pre-gate localStorage key so existing failure history carries over
const IDENTIFIER_FAILURES_KEY = 'login_failure_counts';
const GATE_FLAG_KEY = 'ta_auth_gate_required';
const PASS_TOKEN_KEY = 'ta_captcha_pass';

export const AUTH_GATE_FAILURE_THRESHOLD = 3;

// ── Types (mirror src/lib/captchaGate.ts server payloads) ───────

export type GateStepType = 'text' | 'math' | 'select';

export interface GateStep {
  id: string;
  type: GateStepType;
  /** SVG markup — challenge content is always an image, never DOM text */
  image: string;
  prompt?: string;
  gridSize?: number;
  targetCount?: number;
}

export type GateSubmitStatus = 'continue' | 'passed' | 'failed' | 'invalid';

export type GateStatus =
  | 'idle'        // closed
  | 'loading'     // fetching a step
  | 'step'        // a step is on screen
  | 'submitting'  // an answer is being verified
  | 'passed'      // brief success state before the gate closes
  | 'failed'      // soft-failed — offer a fresh attempt
  | 'blocked';    // too many failed sessions — cooling off

export interface GateSubmitResult {
  status: GateSubmitStatus;
  step?: GateStep;
  passToken?: string;
  blocked?: boolean;
  retryAfter?: number;
}

// ── Flag / token / counter storage ──────────────────────────────

function safeGet(store: Storage, key: string): string | null {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(store: Storage, key: string, value: string): void {
  try {
    store.setItem(key, value);
  } catch {
    // ignore
  }
}

function safeRemove(store: Storage, key: string): void {
  try {
    store.removeItem(key);
  } catch {
    // ignore
  }
}

function getFailureCounts(): Record<string, number> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = safeGet(localStorage, IDENTIFIER_FAILURES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeFailureCounts(counts: Record<string, number>): void {
  if (typeof window === 'undefined') return;
  safeSet(localStorage, IDENTIFIER_FAILURES_KEY, JSON.stringify(counts));
}

// Identifiers are normalized so 'User@x.com' and 'user@x.com' share a counter
function normalizeIdentifier(identifier: string): string {
  return identifier.toLowerCase().trim();
}

export function getIdentifierFailureCount(identifier: string): number {
  const key = normalizeIdentifier(identifier);
  if (!key) return 0;
  return getFailureCounts()[key] || 0;
}

export function recordIdentifierFailure(identifier: string): number {
  const key = normalizeIdentifier(identifier);
  if (!key || typeof window === 'undefined') return 0;
  const counts = getFailureCounts();
  const next = (counts[key] || 0) + 1;
  counts[key] = next;
  writeFailureCounts(counts);
  return next;
}

export function clearIdentifierFailures(identifier: string): void {
  const key = normalizeIdentifier(identifier);
  if (!key || typeof window === 'undefined') return;
  const counts = getFailureCounts();
  delete counts[key];
  writeFailureCounts(counts);
}

export function clearAllIdentifierFailures(): void {
  if (typeof window === 'undefined') return;
  safeRemove(localStorage, IDENTIFIER_FAILURES_KEY);
}

/** Any identifier at/above threshold means this browser is suspicious. */
export function hasFlaggedIdentifier(): boolean {
  return Object.values(getFailureCounts()).some(count => count >= AUTH_GATE_FAILURE_THRESHOLD);
}

/** Server-forced flag — set when an auth response carries requires_captcha. */
export function isGateFlagged(): boolean {
  if (typeof window === 'undefined') return false;
  return safeGet(sessionStorage, GATE_FLAG_KEY) === '1';
}

export function markGateRequired(): void {
  if (typeof window === 'undefined') return;
  safeSet(sessionStorage, GATE_FLAG_KEY, '1');
}

export function clearGateFlag(): void {
  if (typeof window === 'undefined') return;
  safeRemove(sessionStorage, GATE_FLAG_KEY);
}

export function getCaptchaPassToken(): string | null {
  if (typeof window === 'undefined') return null;
  return safeGet(sessionStorage, PASS_TOKEN_KEY);
}

export function storeCaptchaPassToken(token: string): void {
  if (typeof window === 'undefined') return;
  safeSet(sessionStorage, PASS_TOKEN_KEY, token);
}

export function clearCaptchaPassToken(): void {
  if (typeof window === 'undefined') return;
  safeRemove(sessionStorage, PASS_TOKEN_KEY);
}

/**
 * Whether the gate must block the auth pages right now.
 * A stored pass token suppresses the gate — if it turns out to be stale,
 * the next auth call returns requires_captcha and the gate re-opens.
 */
export function isAuthGateRequired(): boolean {
  if (getCaptchaPassToken()) return false;
  return isGateFlagged() || hasFlaggedIdentifier();
}

// ── Gate API ────────────────────────────────────────────────────

export async function startGateSession(): Promise<{ sessionId: string; step: GateStep }> {
  const response = await fetch(buildApiUrl('/api/v1/auth/captcha-gate'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getPortalAppHeaders() },
    body: JSON.stringify({}),
  });
  const data = await response.json().catch(() => ({}));

  if (response.status === 429 || data?.blocked) {
    const err = new Error(data?.error || 'Too many attempts. Please wait and try again.') as Error & {
      blocked?: boolean;
      retryAfter?: number;
    };
    err.blocked = true;
    err.retryAfter = typeof data?.retry_after === 'number' ? data.retry_after : 600;
    throw err;
  }

  if (!response.ok || !data?.success || !data?.session_id || !data?.step) {
    throw new Error(data?.error || 'Failed to load security check');
  }

  return { sessionId: data.session_id, step: data.step as GateStep };
}

export async function submitGateAnswer(
  sessionId: string,
  stepId: string,
  answer: string | number[],
): Promise<GateSubmitResult> {
  const response = await fetch(buildApiUrl('/api/v1/auth/captcha-gate/submit'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getPortalAppHeaders() },
    body: JSON.stringify({ session_id: sessionId, step_id: stepId, answer }),
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (data?.error === 'session_expired' || data?.status === 'invalid') {
      return { status: 'invalid' };
    }
    throw new Error(data?.error || 'Failed to verify security check');
  }

  return {
    status: (data?.status || 'invalid') as GateSubmitStatus,
    step: data?.step,
    passToken: data?.pass_token,
    blocked: data?.blocked === true,
    retryAfter: typeof data?.retry_after === 'number' ? data.retry_after : undefined,
  };
}
